/* eslint-disable react/function-component-definition */
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { TextField, useTheme, useMediaQuery, IconButton, Tooltip } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import Autocomplete from "@mui/material/Autocomplete";
import Swal from "sweetalert2";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import api from "api/api";
import useAccountMemberRecSheetData, {
  parseNumber,
  formatNumber,
} from "./accountMemberRecSheetData";
import LoadingScreen from "layouts/loading/loadingscreen";
import { API_BASE_URL } from "config";
import { buildFileDownloadUrl } from "utils/fileDownloadUrl";
import {
  canEditSensitiveField,
  maskSensitiveFieldValue,
} from "utils/maskingUtils";
import PreviewOverlay from "utils/PreviewOverlay";
import {
  HEADOFFICE_DOCUMENT_FILE_ACCEPT,
  getHeadOfficeDocumentPreviewKind,
  isHeadOfficeDocumentSupportedFile,
} from "utils/headOfficeDocumentImageUtils";

const NUMERIC_COLS = new Set(["salary"]);
const PREVIEW_FILE_FIELDS = ["employment_contract", "id", "bankbook"];
const IMAGE_FIELDS = new Set(["employment_contract", "id", "bankbook"]);
const PREVIEW_FIELD_LABELS = {
  employment_contract: "근로계약서",
  id: "신분증",
  bankbook: "통장사본",
};

const STICKY_COL_WIDTH = {
  cor_type: 90,
  name: 80,
  rrn: 104,
};

const STICKY_LEFT = {
  cor_type: 0,
  name: STICKY_COL_WIDTH.cor_type,
  rrn: STICKY_COL_WIDTH.cor_type + STICKY_COL_WIDTH.name,
  account_id: STICKY_COL_WIDTH.cor_type + STICKY_COL_WIDTH.name + STICKY_COL_WIDTH.rrn,
};

const DEFAULT_JOIN_DATE_SORT = [{ id: "act_join_dt", desc: false }];
const DATE_FIELDS = new Set(["act_join_dt"]);
const SELECT_FIELDS = new Set([
  "contract_type",
  "position_type",
  "start_time",
  "end_time",
  "account_id",
  "idx",
  "use_yn",
  "cor_type",
]);
const NON_EDITABLE_COLS = new Set(["total"]);
const CENTER_ALIGN_COLS = new Set([
  "rrn",
  "account_number",
  "phone",
  "contract_type",
  "act_join_dt",
  "idx",
  "start_time",
  "end_time",
  "use_yn",
]);

const POSITION_OPTIONS = [
  { value: "1", label: "영양사" },
  { value: "2", label: "조리팀장" },
  { value: "3", label: "조리장" },
  { value: "4", label: "조리사" },
  { value: "5", label: "조리원" },
  { value: "6", label: "유틸" },
  { value: "7", label: "통합" },
];

const CONTRACT_OPTIONS = [
  { value: "1", label: "4대보험" },
  { value: "2", label: "프리랜서" },
];

const USE_YN_OPTIONS = [
  { value: "D", label: "진행중" },
  { value: "Y", label: "채용확정" },
  { value: "N", label: "채용취소" },
];

const COR_OPTIONS = [
  { value: "1", label: "(주)더채움" },
  { value: "2", label: "더채움" },
];

const ACTIVE_STATUS_OPTIONS = [
  { value: "D", label: "진행중" },
  { value: "Y", label: "채용확정" },
  { value: "N", label: "채용취소" },
];

// ✅ 엑셀 다운로드 컬럼(순서/헤더는 화면 표에 맞춤, act_join_dt는 화면에서 정렬용 헤더라 별도 라벨 지정)
const EXCEL_COLUMNS = [
  { key: "cor_type", header: "구분", width: 12 },
  { key: "name", header: "성명", width: 12 },
  { key: "rrn", header: "주민번호", width: 16 },
  { key: "account_id", header: "업장명", width: 20 },
  { key: "position_type", header: "직책", width: 10 },
  { key: "account_number", header: "계좌번호", width: 20 },
  { key: "phone", header: "연락처", width: 15 },
  { key: "address", header: "주소", width: 26 },
  { key: "contract_type", header: "계약형태", width: 12 },
  { key: "act_join_dt", header: "실입사일", width: 12 },
  { key: "salary", header: "급여(월)", width: 12 },
  { key: "idx", header: "근무형태", width: 20 },
  { key: "start_time", header: "시작", width: 8 },
  { key: "end_time", header: "마감", width: 8 },
  { key: "use_yn", header: "채용여부", width: 10 },
  { key: "note", header: "비고", width: 24 },
  { key: "employment_contract", header: "근로계약서", width: 24 },
  { key: "id", header: "신분증", width: 24 },
  { key: "bankbook", header: "통장사본", width: 24 },
];

const generateTimeOptions = (startHHMM, endHHMM, stepMinutes = 30) => {
  const toMinutes = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    return h * 60 + m;
  };
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  const start = toMinutes(startHHMM);
  const end = toMinutes(endHHMM);
  const arr = [];
  for (let t = start; t <= end; t += stepMinutes) {
    const hh = Math.floor(t / 60);
    const mm = t % 60;
    arr.push(`${hh}:${pad(mm)}`);
  }
  return arr;
};

const START_TIMES = generateTimeOptions("5:30", "16:00", 30);
const END_TIMES = generateTimeOptions("10:00", "20:00", 30);

// 운영 -> 채용관리 -> 현장 채용현황
function AccountMemberRecSheet() {
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const accountInputRef = useRef("");
  const [activeStatus, setActiveStatus] = useState("Y");
  const tableContainerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isTabletOrSmallDesktop = useMediaQuery("(max-width:1280px)");
  const [maskingEnabled, setMaskingEnabled] = useState(true);
  const maskingRole = useMemo(() => {
    if (typeof window === "undefined" || !window?.localStorage) {
      return { department: "", position: "", user_id: "" };
    }

    const toCode = (value) => String(value ?? "").trim();
    return {
      department: toCode(window.localStorage.getItem("department")),
      position: toCode(window.localStorage.getItem("position")),
      user_id: toCode(window.localStorage.getItem("user_id")),
    };
  }, []);

  const {
    activeRows,
    setActiveRows,
    originalRows,
    setOriginalRows,
    accountList,
    workSystemList,
    fetchAccountMembersAllList,
  } = useAccountMemberRecSheetData(selectedAccountId, activeStatus);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [excelDownloading, setExcelDownloading] = useState(false);
  const [isColumnWidthLocked, setIsColumnWidthLocked] = useState(false);
  const [measuredColWidths, setMeasuredColWidths] = useState({});
  const [relockAfterMeasure, setRelockAfterMeasure] = useState(false);

  // ✅ 정렬 상태 (실입사일 -> 업장명)
  const [sorting, setSorting] = useState(DEFAULT_JOIN_DATE_SORT);

  // ✅ 스냅샷 갱신 트리거 (재조회/저장 성공 시 올려줌)
  const [snapshotTick, setSnapshotTick] = useState(0);

  // 첨부파일 미리보기 오버레이 상태
  const [previewFiles, setPreviewFiles] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const previewObjectUrlsRef = useRef([]);

  const normalizeTime = (t) => {
    if (!t) return "";
    return String(t)
      .trim()
      .replace(/^0(\d):/, "$1:");
  };

  const keepEditableTailVisible = useCallback((el) => {
    if (!el) return;
    window.requestAnimationFrame(() => {
      el.scrollLeft = el.scrollWidth;
    });
  }, []);

  const lockColumnsForEditing = useCallback(() => {
    setIsColumnWidthLocked((prev) => (prev ? prev : true));
  }, []);

  // ✅ row 고정키(_rid) 보장: 정렬해도 원본매칭/수정대상 식별 유지
  const ensureRowId = useCallback((row) => {
    if (!row) return row;
    if (row._rid) return row;

    const base = row.member_id ?? row.rrn ?? "";
    if (base) return { ...row, _rid: String(base) };

    return { ...row, _rid: `NEW_${Date.now()}_${Math.random().toString(16).slice(2)}` };
  }, []);

  // _rid가 없는 초기 조회 데이터도 안정적으로 같은 행으로 인식하기 위한 키
  const getStableRowKey = useCallback(
    (row, index = "") =>
      String(row?._rid ?? row?.member_id ?? row?.rrn ?? (index !== "" ? `ROW_${index}` : "")),
    []
  );

  // 조회
  useEffect(() => {
    // ✅ 조회 상태는 항상 전체 텍스트 노출(열 자동 확장)
    setIsColumnWidthLocked(false);
    setLoading(true);
    fetchAccountMembersAllList()
      .then(() => {
        // ✅ 재조회 완료 시 스냅샷 갱신 트리거
        setSnapshotTick((t) => t + 1);
      })
      .finally(() => setLoading(false));
  }, [selectedAccountId, activeStatus]);

  // total 계산
  const calculateTotal = (row) => {
    const breakfast = parseNumber(row.breakfast);
    const lunch = parseNumber(row.lunch);
    const dinner = parseNumber(row.dinner);
    const ceremony = parseNumber(row.ceremony);
    const avgMeals = (breakfast + lunch + dinner) / 3;
    return Math.round(avgMeals + ceremony);
  };

  /**
   * ✅ 스냅샷 최신화
   */
  useEffect(() => {
    if (!activeRows || activeRows.length === 0) {
      setOriginalRows([]);
      return;
    }

    const updated = activeRows.map((row) => {
      const r = ensureRowId(row);
      return { ...r, total: calculateTotal(r) };
    });

    setActiveRows(updated);
    setOriginalRows(updated.map((r) => ({ ...r })));
  }, [snapshotTick]); // ✅ 여기!

  const formatDateForInput = (val) => {
    if (!val && val !== 0) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    try {
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().slice(0, 10);
    } catch {
      return "";
    }
  };

  const toDateMs = (v) => {
    if (!v) return 0;
    if (v instanceof Date) return v.getTime();
    const s = String(v).trim();
    if (!s) return 0;
    const ms = Date.parse(s);
    return Number.isNaN(ms) ? 0 : ms;
  };

  const columns = useMemo(
    () => {
      const accountNameMap = new Map(
        (accountList || []).map((acc) => [String(acc.account_id ?? ""), String(acc.account_name ?? "")])
      );
      const getAccountName = (accountId) => {
        const key = String(accountId ?? "");
        return accountNameMap.get(key) || key;
      };

      return [
        { header: "구분", accessorKey: "cor_type", size: STICKY_COL_WIDTH.cor_type },
        { header: "성명", accessorKey: "name", size: STICKY_COL_WIDTH.name },
        { header: "주민번호", accessorKey: "rrn", size: STICKY_COL_WIDTH.rrn },
        { header: "업장명", accessorKey: "account_id", size: 150 },
        { header: "직책", accessorKey: "position_type", size: 65 },
        { header: "계좌번호", accessorKey: "account_number", size: 160 },
        { header: "연락처", accessorKey: "phone", size: 100 },
        { header: "주소", accessorKey: "address", size: 150 },
        { header: "계약형태", accessorKey: "contract_type", size: 50 },

        {
          accessorKey: "act_join_dt",
          size: 80,
          enableSorting: true,
          sortingFn: (rowA, rowB, columnId) => {
            const a = toDateMs(rowA.getValue(columnId));
            const b = toDateMs(rowB.getValue(columnId));
            if (a !== b) return a > b ? 1 : -1;

            const accountNameA = getAccountName(rowA.original?.account_id);
            const accountNameB = getAccountName(rowB.original?.account_id);
            return accountNameA.localeCompare(accountNameB, "ko");
          },
          header: ({ column }) => {
            const sorted = column.getIsSorted();
            return (
              <div
                onClick={column.getToggleSortingHandler()}
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                }}
                title="정렬"
              >
                실입사일
                <span style={{ fontSize: 11 }}>
                  {sorted === "asc" ? "▲" : sorted === "desc" ? "▼" : ""}
                </span>
              </div>
            );
          },
        },

        {
          header: "급여(월)",
          accessorKey: "salary",
          size: 80,
          cell: (info) => formatNumber(info.getValue()),
        },
        { header: "근무형태", accessorKey: "idx", size: 180 },
        { header: "시작", accessorKey: "start_time", size: 60 },
        { header: "마감", accessorKey: "end_time", size: 60 },
        { header: "채용여부", accessorKey: "use_yn", size: 60 },
        { header: "비고", accessorKey: "note", minWidth: 80, maxWidth: 150 },
        { header: "근로계약서", accessorKey: "employment_contract", size: 90 },
        { header: "신분증", accessorKey: "id", size: 80 },
        { header: "통장사본", accessorKey: "bankbook", size: 80 },
      ];
    },
    [accountList]
  );

  const accountNameMap = useMemo(
    () =>
      new Map(
        (accountList || []).map((acc) => [String(acc.account_id ?? ""), String(acc.account_name ?? "")])
      ),
    [accountList]
  );
  const getAccountName = useCallback(
    (accountId) => {
      const key = String(accountId ?? "");
      return accountNameMap.get(key) || key;
    },
    [accountNameMap]
  );

  const accountOptions = useMemo(
    () => [
      // 거래처 전체 옵션
      { value: "", label: "전체" },
      ...((accountList || []).map((acc) => ({
        value: String(acc.account_id),
        label: acc.account_name,
      })) || []),
    ],
    [accountList]
  );

  const selectedAccountOption = useMemo(() => {
    const v = String(selectedAccountId ?? "");
    return accountOptions.find((o) => o.value === v) || null;
  }, [accountOptions, selectedAccountId]);

  const workSystemOptions = useMemo(
    () =>
      (workSystemList || []).map((w) => ({
        value: String(w.idx),
        label: w.work_system,
      })),
    [workSystemList]
  );

  const workSystemOptionMap = useMemo(
    () => new Map((workSystemOptions || []).map((o) => [o.value, o])),
    [workSystemOptions]
  );

  const accountSelectOptions = useMemo(
    () =>
      (accountList || []).map((acc) => ({
        value: acc.account_id,
        label: acc.account_name,
      })),
    [accountList]
  );

  // 거래처 검색 성능: 라벨 소문자 캐시(타이핑 시 매번 toLowerCase 반복 방지)
  const accountOptionsIndexed = useMemo(
    () =>
      (accountOptions || []).map((o) => ({
        ...o,
        _labelLower: String(o?.label ?? "").toLowerCase(),
      })),
    [accountOptions]
  );

  const selectAccountByInput = useCallback(() => {
    const q = String(accountInputRef.current || "").trim();
    if (!q) return;
    const list = accountOptionsIndexed || [];
    const qLower = q.toLowerCase();
    const exact = list.find((o) => o._labelLower === qLower);
    const partial = exact || list.find((o) => o._labelLower.includes(qLower));
    if (partial) {
      setSelectedAccountId(partial.value);
    }
  }, [accountOptionsIndexed]);

  const handleToggleMasking = useCallback(() => {
    setMaskingEnabled((prev) => !prev);

    // 수정중(고정폭)에서 마스킹 상태가 바뀌면
    // 새 표시 기준으로 자동폭 재측정 후 다시 고정한다.
    if (isColumnWidthLocked) {
      setRelockAfterMeasure(true);
      setIsColumnWidthLocked(false);
    }
  }, [isColumnWidthLocked]);

  const table = useReactTable({
    data: activeRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
    getRowId: (row, index) => getStableRowKey(row, index),
  });

  // 조회/저장 후(auto 모드) 실제 렌더링된 헤더 너비를 기준 폭으로 저장
  useEffect(() => {
    if (loading || isColumnWidthLocked) return;

    const rafId = window.requestAnimationFrame(() => {
      const tableEl = tableContainerRef.current?.querySelector("table");
      if (!tableEl) return;

      const headerCells = tableEl.querySelectorAll("thead th");
      if (!headerCells || headerCells.length === 0) return;

      const next = {};
      table.getFlatHeaders().forEach((header, idx) => {
        const el = headerCells[idx];
        if (!el) return;
        const w = Math.ceil(el.getBoundingClientRect().width);
        if (w > 0) next[header.id] = w;
      });

      if (Object.keys(next).length > 0) {
        setMeasuredColWidths(next);
      }
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [loading, isColumnWidthLocked, table, activeRows, sorting, maskingEnabled]);

  useEffect(() => {
    if (!relockAfterMeasure || isColumnWidthLocked) return;
    if (Object.keys(measuredColWidths || {}).length === 0) return;

    setIsColumnWidthLocked(true);
    setRelockAfterMeasure(false);
  }, [relockAfterMeasure, isColumnWidthLocked, measuredColWidths]);

  // ✅ 원본을 _rid로 매칭
  const originalMap = useMemo(() => {
    const m = new Map();
    (originalRows || []).forEach((r, idx) => m.set(getStableRowKey(r, idx), r));
    return m;
  }, [originalRows, getStableRowKey]);

  const updateRowByRid = (rid, patch) => {
    setActiveRows((prev) =>
      (prev || []).map((r, idx) =>
        getStableRowKey(r, idx) === String(rid) ? { ...r, ...patch } : r
      )
    );
  };

  // =========================================================
  // ✅ 엑셀 다운로드: 현재 화면에 걸린 필터(채용상태/거래처) + 정렬 그대로 반영해서 다운로드
  //    - 마스킹은 권한 때문에 하는 것이므로 화면에 지금 보이는 그대로(해제 안 됐으면 마스킹된 채로,
  //      해제됐으면 해제된 값 그대로) 엑셀에 담는다. 화면 표시 로직(maskSensitiveFieldValue)을 그대로 재사용.
  // =========================================================
  const mapOptionLabel = (options, value) => {
    const key = String(value ?? "");
    return options.find((o) => o.value === key)?.label ?? key;
  };

  // 근로계약서/신분증/통장사본: 실제 파일명은 빼고 그 앞 경로까지만 노출
  const stripFileName = (path) => {
    const raw = String(path ?? "").trim();
    if (!raw) return "";
    const idx = raw.lastIndexOf("/");
    return idx >= 0 ? raw.slice(0, idx + 1) : "";
  };

  const handleExcelDownload = async () => {
    if (excelDownloading) return;
    setExcelDownloading(true);

    try {
      // ✅ 화면에 보이는 순서(현재 정렬 상태) 그대로 뽑는다
      const rowsInScreenOrder = table.getSortedRowModel().rows.map((r) => r.original);

      const wb = new ExcelJS.Workbook();
      wb.creator = "AccountMemberRecSheet";
      const ws = wb.addWorksheet("현장 채용현황");

      ws.columns = EXCEL_COLUMNS.map((c) => ({ key: c.key, width: c.width }));

      const statusLabel = mapOptionLabel(ACTIVE_STATUS_OPTIONS, activeStatus);
      const accountLabel = selectedAccountId ? getAccountName(selectedAccountId) : "전체";
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      const todayLabel = `${y}-${m}-${d}`;

      // 제목행
      ws.addRow([`■ 현장 채용현황 / 상태: ${statusLabel} / 업장: ${accountLabel} / ${todayLabel}`]);
      const titleRowNum = ws.lastRow.number;
      ws.mergeCells(titleRowNum, 1, titleRowNum, EXCEL_COLUMNS.length);
      ws.getCell(titleRowNum, 1).font = { bold: true, size: 12 };
      ws.getRow(titleRowNum).height = 26;

      // 마스킹 해제가 안 됐으면 참고용 안내를 한 줄 더 남긴다(권한 때문에 마스킹된 채로 내려간다는 표시)
      if (maskingEnabled) {
        ws.addRow(["※ 마스킹 해제 전 상태로 다운로드되었습니다. (주민번호/연락처/계좌번호/주소 일부 마스킹)"]);
        const noteRowNum = ws.lastRow.number;
        ws.mergeCells(noteRowNum, 1, noteRowNum, EXCEL_COLUMNS.length);
        ws.getCell(noteRowNum, 1).font = { italic: true, size: 10, color: { argb: "FF888888" } };
      }

      // 헤더행
      const header = EXCEL_COLUMNS.map((c) => c.header);
      ws.addRow(header);
      const headerRowNum = ws.lastRow.number;
      const headerRow = ws.getRow(headerRowNum);
      headerRow.font = { bold: true };
      headerRow.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
      headerRow.height = 22;
      headerRow.eachCell((cell) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF0F0F0" } };
      });

      const salaryColIndex = EXCEL_COLUMNS.findIndex((c) => c.key === "salary") + 1;

      // ✅ 내용이 잘려서 안 보이는 일이 없도록, 실제 값 길이를 보고 열 너비를 계산한다
      const autoWidthValues = EXCEL_COLUMNS.map((c) => [c.header]);

      rowsInScreenOrder.forEach((row) => {
        const rowData = {};
        EXCEL_COLUMNS.forEach((c, colIdx) => {
          const key = c.key;
          let value = row[key];

          if (key === "cor_type") value = mapOptionLabel(COR_OPTIONS, value);
          else if (key === "contract_type") value = mapOptionLabel(CONTRACT_OPTIONS, value);
          else if (key === "position_type") value = mapOptionLabel(POSITION_OPTIONS, value);
          else if (key === "use_yn") value = mapOptionLabel(USE_YN_OPTIONS, value);
          else if (key === "account_id") value = getAccountName(value);
          else if (key === "idx") value = workSystemOptionMap.get(String(value ?? ""))?.label ?? String(value ?? "");
          else if (key === "salary") value = parseNumber(value);
          else if (IMAGE_FIELDS.has(key)) {
            // 저장 전(로컬 파일 객체) 상태는 다운로드에 포함할 경로가 없어 안내 문구로 대체,
            // 저장된 경로는 파일명 빼고 그 앞 경로까지만 노출
            value = typeof value === "object" && value ? "(저장 전 첨부)" : stripFileName(value);
          } else {
            // ✅ 화면과 동일하게 마스킹 여부/권한을 그대로 반영해서 값 결정
            value = maskSensitiveFieldValue(key, value, maskingEnabled, maskingRole) ?? "";
          }

          rowData[key] = value ?? "";
          autoWidthValues[colIdx].push(key === "salary" ? formatNumber(value) : value);
        });
        ws.addRow(rowData);
        const rowNum = ws.lastRow.number;
        // ✅ 행 높이를 고정하지 않고 그대로 둬서, 긴 텍스트(주소/비고 등)가 줄바꿈될 때
        //    엑셀에서 열어봤을 때 잘리지 않고 전체 내용이 보이도록 한다
        ws.getRow(rowNum).alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        ws.getRow(rowNum).eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
        if (salaryColIndex > 0) {
          const salaryCell = ws.getRow(rowNum).getCell(salaryColIndex);
          if (typeof salaryCell.value === "number") {
            salaryCell.numFmt = "#,##0";
            salaryCell.alignment = { vertical: "middle", horizontal: "right", wrapText: true };
          }
        }
      });

      // ✅ 실제로 들어간 값 중 가장 긴 걸 기준으로 열 너비 재계산(너무 좁아서 내용이 가려지는 것 방지)
      const calcWidth = (values, min, max = 60) => {
        const longest = Math.max(...values.map((v) => String(v ?? "").length), 0);
        return Math.min(Math.max(longest + 2, min), max);
      };
      autoWidthValues.forEach((values, colIdx) => {
        const baseWidth = EXCEL_COLUMNS[colIdx].width;
        ws.getColumn(colIdx + 1).width = calcWidth(values, baseWidth);
      });

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `현장채용현황_${statusLabel}_${accountLabel}_${todayLabel}.xlsx`);
    } catch (err) {
      Swal.fire("다운로드 실패", err?.message || "엑셀 생성 중 오류가 발생했습니다.", "error");
    } finally {
      setExcelDownloading(false);
    }
  };

  const clearPreviewObjectUrls = useCallback(() => {
    previewObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewObjectUrlsRef.current = [];
  }, []);

  const getPreviewKind = useCallback(
    (value) =>
      typeof value === "object"
        ? getHeadOfficeDocumentPreviewKind(value)
        : getHeadOfficeDocumentPreviewKind({ image_path: value }),
    []
  );

  const toPreviewUrl = useCallback((value) => {
    if (!value) return "";
    if (typeof value === "object") {
      const objectUrl = URL.createObjectURL(value);
      previewObjectUrlsRef.current.push(objectUrl);
      return objectUrl;
    }
    const path = String(value || "");
    if (/^(https?:\/\/|blob:|data:)/i.test(path)) return path;
    const base = String(API_BASE_URL || "").replace(/\/+$/, "");
    const params = new URLSearchParams();
    params.set("file_path", path.startsWith("/") ? path : `/${path}`);
    return `${base}/Account/AccountStoredFileView?${params.toString()}`;
  }, []);

  const handleViewImage = useCallback(
    (rowData, field) => {
      clearPreviewObjectUrls();
      const files = PREVIEW_FILE_FIELDS
        .map((key) => {
          const value = rowData?.[key];
          if (!value) return null;
          return {
            url: toPreviewUrl(value),
            name: PREVIEW_FIELD_LABELS[key],
            kind: getPreviewKind(value),
          };
        })
        .filter(Boolean);

      const targetIndex = Math.max(0, files.findIndex((file) => file.name === PREVIEW_FIELD_LABELS[field]));
      setPreviewFiles(files);
      setPreviewIndex(targetIndex);
    },
    [clearPreviewObjectUrls, getPreviewKind, toPreviewUrl]
  );

  const handleCloseViewer = useCallback(() => {
    setPreviewFiles([]);
    setPreviewIndex(0);
    clearPreviewObjectUrls();
  }, [clearPreviewObjectUrls]);

  const handleDownload = useCallback((path) => {
    if (!path || typeof path !== "string") return;
    const url = buildFileDownloadUrl(path);
    const filename = path.split("/").pop() || "download";
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  const fileIconSx = { color: "#1e88e5" };

  const uploadImage = async (file, field, row) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "member");
    const gubun = `${field}_${row.member_id || row.rrn || Date.now()}`;
    formData.append("gubun", gubun);
    formData.append("folder", row.account_id || selectedAccountId || "common");

    const res = await api.post("/Operate/OperateImgUpload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (res.data.code === 200) return res.data.image_path;
    throw new Error(res.data.message || "이미지 업로드 실패");
  };

  // =========================================================
  // ✅ 채용확정(Y) 중복 체크: /Operate/AccountMemberAllList
  //   - param: { account_id, del_yn: "N" }
  //   - 비교: name, rrn, account_id
  //   - 동일 member_id는 자기 자신이므로 중복 제외
  // =========================================================
  const fetchAccountMemberAllList = async (account_id) => {
    const params = {};
    params.account_id = account_id;
    params.del_yn = "N";

    const res = await api.get("/Operate/AccountMemberAllList", {
      params,
    });

    // ✅ 백엔드 응답 키가 어떤 형태든 최대한 배열을 찾아서 사용
    const payload = res.data;
    const list =
      payload?.data ??
      payload?.result ??
      payload?.list ??
      payload?.rows ??
      payload?.items ??
      payload;

    return Array.isArray(list) ? list : [];
  };

  const normalizeKey = (v) => String(v ?? "").trim();

  const handleSave = async () => {
    handleCloseViewer();
    if (saving) return;

    const originalByRid = new Map(
      (originalRows || []).map((r, idx) => [getStableRowKey(r, idx), r])
    );

    const changedRowsWithRid = (activeRows || [])
      .map((row, idx) => ({ row, rid: getStableRowKey(row, idx) }))
      .filter(({ row, rid }) => {
        const original = originalByRid.get(rid);
        if (!original) return true;

        return Object.keys(row).some((key) => {
          if (IMAGE_FIELDS.has(key)) {
            const v = row[key];
            const o = original[key];
            if (typeof v === "object" && v) return true;
            return String(v ?? "") !== String(o ?? "");
          }
          if (NUMERIC_COLS.has(key)) {
            return Number(row[key] ?? 0) !== Number(original[key] ?? 0);
          }
          return String(row[key] ?? "") !== String(original[key] ?? "");
        });
      });
    const changedRows = changedRowsWithRid.map((item) => item.row);

    if (changedRows.length === 0) {
      Swal.fire("저장할 변경사항이 없습니다.", "", "info");
      return;
    }

    setSaving(true);
    setLoading(true);

    try {
      const userId = localStorage.getItem("user_id");

      const cleanRow = (row) => {
        const newRow = { ...row };
        Object.keys(newRow).forEach((key) => {
          if (newRow[key] === "" || newRow[key] === undefined) newRow[key] = null;
        });
        return newRow;
      };

      // =========================================================
      // ✅ 1) 채용확정(Y)인 행들만 중복 체크
      //    - (name, rrn, account_id) 동일한 기존 데이터가 있으면 저장 막기
      // =========================================================
      const mustCheckRows = changedRows.filter((r) => String(r.use_yn ?? "") === "Y");

      // account_id 없는 경우는 체크 불가 → 저장 막고 안내
      const invalidAccountRows = mustCheckRows.filter((r) => !normalizeKey(r.account_id));
      if (invalidAccountRows.length > 0) {
        Swal.fire(
          "저장 불가",
          "채용확정(Y) 행에 업장명이 비어있는 항목이 있습니다. 업장명을 선택 후 저장해주세요.",
          "warning"
        );
        return;
      }

      // account_id별로 1번만 조회하도록 캐시
      const cache = new Map(); // account_id -> list
      const getListByAccount = async (account_id) => {
        const key = normalizeKey(account_id);
        if (cache.has(key)) return cache.get(key);
        const list = await fetchAccountMemberAllList(key);
        cache.set(key, list);
        return list;
      };

      const duplicates = [];
      for (const row of mustCheckRows) {
        const accId = normalizeKey(row.account_id);
        const list = await getListByAccount(accId);

        const rowName = normalizeKey(row.name);
        const rowRrn = normalizeKey(row.rrn);
        const rowMemberId = normalizeKey(row.member_id);

        const found = list.find((m) => {
          const mName = normalizeKey(m.name);
          const mRrn = normalizeKey(m.rrn);
          const mAcc = normalizeKey(m.account_id);
          const mMemberId = normalizeKey(m.member_id);

          // ✅ 자기 자신이면 중복 제외
          if (rowMemberId && mMemberId && rowMemberId === mMemberId) return false;

          return mName === rowName && mRrn === rowRrn && mAcc === accId;
        });

        if (found) {
          duplicates.push({
            name: row.name,
            rrn: row.rrn,
            account_id: row.account_id,
          });
        }
      }

      if (duplicates.length > 0) {
        const msg = duplicates
          .slice(0, 10)
          .map((d) => `- ${d.name || ""} / ${d.rrn || ""} / ${d.account_id || ""}`)
          .join("<br/>");

        Swal.fire({
          icon: "warning",
          title: "저장 불가",
          html:
            `채용확정(Y) 항목 중 이미 등록된 직원이 있습니다.<br/>` +
            `성명/주민번호/업장명이 동일한 데이터는 저장할 수 없습니다.<br/><br/>` +
            `${msg}` +
            (duplicates.length > 10 ? `<br/><br/>외 ${duplicates.length - 10}건` : ""),
        });
        return;
      }

      // =========================================================
      // ✅ 2) 이미지 업로드 포함 저장 데이터 가공
      // =========================================================
      const processedWithRid = await Promise.all(
        changedRowsWithRid.map(async ({ row, rid }) => {
          const newRow = cleanRow(row);

          for (const field of IMAGE_FIELDS) {
            if (newRow[field] && typeof newRow[field] === "object") {
              const uploadedPath = await uploadImage(newRow[field], field, newRow);
              newRow[field] = uploadedPath;
            }
          }

          return { rid, row: { ...newRow, user_id: userId } };
        })
      );
      const processed = processedWithRid.map((item) => item.row);

      const res = await api.post("/Operate/AccountRecMembersSave", { data: processed });

      if (res.data.code === 200) {
        const savedRowByRid = new Map(processedWithRid.map((item) => [item.rid, item.row]));
        const nextRows = (activeRows || []).map((row, idx) => {
          const rid = getStableRowKey(row, idx);
          return savedRowByRid.has(rid) ? { ...row, ...savedRowByRid.get(rid) } : row;
        });
        setActiveRows(nextRows);
        setOriginalRows(nextRows.map((row) => ({ ...row })));
        // ✅ 저장 성공 후 재조회 + 스냅샷 갱신 트리거
        await fetchAccountMembersAllList();
        setSnapshotTick((t) => t + 1);
        // ✅ 저장 완료 후에만 실입사일 정렬 재적용
        setSorting(DEFAULT_JOIN_DATE_SORT);
        // ✅ 저장 성공 후에는 고정 폭 해제 (내용 길이만큼 열 확장)
        setIsColumnWidthLocked(false);

        await Swal.fire("저장 완료", "변경사항이 저장되었습니다.", "success");
      } else {
        Swal.fire("저장 실패", res.data.message || "서버 오류", "error");
      }
    } catch (err) {
      Swal.fire("저장 실패", err.message || String(err), "error");
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  const handleAddRow = () => {
    // ✅ 행추가 직후에는 실입사일 정렬을 잠시 끄고 직접 입력에 집중
    //    저장 완료 후(handleSave 성공) 다시 기본 정렬을 적용한다.
    setSorting([]);
    lockColumnsForEditing();

    const defaultAccountId = selectedAccountId || (accountList?.[0]?.account_id ?? "");
    const defaultWorkSystemIdx = workSystemList?.[0]?.idx ? String(workSystemList[0].idx) : "";

    const newRow = {
      _rid: `NEW_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      name: "",
      rrn: "",
      address: "",
      account_id: defaultAccountId,
      contract_type: "1",
      position_type: "1",
      act_join_dt: "",
      salary: "",
      idx: defaultWorkSystemIdx,
      start_time: workSystemList?.[0]?.start_time
        ? normalizeTime(workSystemList[0].start_time)
        : START_TIMES?.[0] ?? "5:30",
      end_time: workSystemList?.[0]?.end_time
        ? normalizeTime(workSystemList[0].end_time)
        : END_TIMES?.[0] ?? "10:00",
      use_yn: "D",
      note: "",
      employment_contract: "",
      id: "",
      bankbook: "",
      cor_type: 1,
    };

    setActiveRows((prev) => [newRow, ...(prev || [])]);
    setOriginalRows((prev) => [newRow, ...(prev || [])]);
  };

  const renderTable = (tableInstance) => {
    // 화면 크기별 고정 열 범위
    // 모바일: 이름(2열)까지 고정, 태블릿: 주민번호(3열)까지 고정, 데스크탑: 기존(4열) 유지
    const stickyColumnCount = isMobile ? 2 : isTablet ? 3 : 4;

    return (
      <MDBox
        ref={tableContainerRef}
        pt={0}
        sx={{
          flex: 1,
          minHeight: 0,
          maxHeight: isMobile ? "55vh" : "75vh",
          overflowX: "auto",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          "& table": {
            borderCollapse: "separate",
            width: "max-content",
            minWidth: "100%",
            borderSpacing: 0,
            tableLayout: isColumnWidthLocked ? "fixed" : "auto",
          },
          "& th, & td": {
            border: "1px solid #686D76",
            textAlign: "center",
            padding: "4px",
            whiteSpace: "nowrap",
            fontSize: "12px",
            verticalAlign: "middle",
          },
          "& th": {
            backgroundColor: "#f0f0f0",
            position: "sticky",
            top: 0,
            zIndex: 2,
          },
          ...(stickyColumnCount >= 1
            ? {
              "& td:nth-of-type(1), & th:nth-of-type(1)": {
                position: "sticky",
                left: STICKY_LEFT.cor_type,
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 2
            ? {
              "& td:nth-of-type(2), & th:nth-of-type(2)": {
                position: "sticky",
                left: `${STICKY_LEFT.name}px`,
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 3
            ? {
              "& td:nth-of-type(3), & th:nth-of-type(3)": {
                position: "sticky",
                left: `${STICKY_LEFT.rrn}px`,
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          ...(stickyColumnCount >= 4
            ? {
              "& td:nth-of-type(4), & th:nth-of-type(4)": {
                position: "sticky",
                left: `${STICKY_LEFT.account_id}px`,
                background: "#f0f0f0",
                zIndex: 3,
              },
            }
            : {}),
          [`thead th:nth-of-type(-n+${stickyColumnCount})`]: { zIndex: 5 },
          "& .edited-cell": { color: "#d32f2f", fontWeight: 500 },
          "td[contenteditable]": {
            minWidth: "80px",
            cursor: "text",
            ...(isColumnWidthLocked
              ? {
                overflowX: "auto",
                overflowY: "hidden",
                whiteSpace: "nowrap",
                msOverflowStyle: "none",
                scrollbarWidth: "none",
              }
              : {}),
          },
          "& td[contenteditable]::-webkit-scrollbar": { display: "none" },
          "& select": {
            fontSize: "12px",
            padding: "4px",
            minWidth: "80px",
            border: "none",
            background: "transparent",
            outline: "none",
            cursor: "pointer",
          },
          "& select.edited-cell": { color: "#d32f2f", fontWeight: 500 },
          "& input[type='date']": {
            fontSize: "12px",
            padding: "4px",
            minWidth: "80px",
            border: "none",
            background: "transparent",
          },
        }}
      >
        <table className="dinersheet-table">
          <thead>
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const headerWidth =
                    header.column.columnDef.size ?? header.column.columnDef.minWidth ?? 80;
                  const lockedHeaderWidth = measuredColWidths[header.id] ?? headerWidth;

                  return (
                    <th
                      key={header.id}
                      style={{
                        width: isColumnWidthLocked ? lockedHeaderWidth : headerWidth,
                        ...(isColumnWidthLocked
                          ? { minWidth: lockedHeaderWidth, maxWidth: lockedHeaderWidth }
                          : {}),
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {tableInstance.getRowModel().rows.map((row) => {
              const rid = getStableRowKey(row.original, row.index);
              const isNewRow = rid.startsWith("NEW_");

              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const colKey = cell.column.columnDef.accessorKey;
                    const currentValue = row.getValue(colKey);
                    const cellWidth =
                      measuredColWidths[cell.column.id] ??
                      cell.column.columnDef.size ??
                      cell.column.columnDef.minWidth ??
                      80;

                    const originalRow = originalMap.get(rid) || {};
                    const originalValue = originalRow?.[colKey];
                    const hasOriginal = originalMap.has(rid);

                    const isNumeric = NUMERIC_COLS.has(colKey);
                    const isImage = IMAGE_FIELDS.has(colKey);

                    const normCurrent = isImage
                      ? typeof currentValue === "object"
                        ? "__FILE__"
                        : String(currentValue ?? "")
                      : isNumeric
                        ? Number(currentValue ?? 0)
                        : String(currentValue ?? "");

                    const normOriginal = isImage
                      ? String(originalValue ?? "")
                      : isNumeric
                        ? Number(originalValue ?? 0)
                        : String(originalValue ?? "");

                    const isChanged = hasOriginal ? normCurrent !== normOriginal : false;

                    const isEditable = !NON_EDITABLE_COLS.has(colKey);
                    const isSelect = SELECT_FIELDS.has(colKey);
                    const isDate = DATE_FIELDS.has(colKey);
                    const canEditMaskedSensitiveField = canEditSensitiveField(
                      colKey,
                      currentValue,
                      maskingEnabled,
                      maskingRole,
                      { isNewRow }
                    );

                    const handleCellChange = (newValue) => {
                      if (colKey === "idx") {
                        const selected = (workSystemList || []).find(
                          (w) => String(w.idx) === String(newValue)
                        );

                        const start_time = selected?.start_time
                          ? normalizeTime(selected.start_time)
                          : row.original?.start_time;

                        const end_time = selected?.end_time
                          ? normalizeTime(selected.end_time)
                          : row.original?.end_time;

                        const next = { idx: newValue, start_time, end_time };

                        const sameIdx = String(row.original?.idx ?? "") === String(next.idx ?? "");
                        const sameStart =
                          String(row.original?.start_time ?? "") === String(next.start_time ?? "");
                        const sameEnd =
                          String(row.original?.end_time ?? "") === String(next.end_time ?? "");
                        if (sameIdx && sameStart && sameEnd) return;

                        lockColumnsForEditing();

                        updateRowByRid(rid, {
                          ...next,
                          total: calculateTotal({ ...row.original, ...next }),
                        });
                        return;
                      }

                      const sameValue =
                        isImage && typeof newValue === "object" && newValue
                          ? false
                          : isNumeric
                            ? Number(row.original?.[colKey] ?? 0) === Number(newValue ?? 0)
                            : String(row.original?.[colKey] ?? "") === String(newValue ?? "");
                      if (sameValue) return;

                      lockColumnsForEditing();

                      const patch = { [colKey]: newValue };
                      updateRowByRid(rid, {
                        ...patch,
                        total: calculateTotal({ ...row.original, ...patch }),
                      });
                    };

                    if (isImage) {
                      const value = currentValue ?? "";
                      const hasImage = !!value;
                      const inputId = `upload-${colKey}-${row.id}`;

                      const rowActionSx = {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        flexWrap: "nowrap",
                        whiteSpace: "nowrap",
                      };

                      const actionBtnSx = {
                        fontSize: isMobile ? "10px" : "11px",
                        minWidth: 44,
                        px: 1,
                        py: 0.5,
                        lineHeight: 1.2,
                        whiteSpace: "nowrap",
                      };

                      const iconBtnSx = { ...fileIconSx, p: 0.5 };

                      return (
                        <td
                          key={cell.id}
                          className={isChanged ? "edited-cell" : ""}
                          style={{
                            textAlign: "center",
                            ...(isColumnWidthLocked
                              ? { width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth }
                              : {}),
                          }}
                        >
                          <input
                            type="file"
                            accept={HEADOFFICE_DOCUMENT_FILE_ACCEPT}
                            id={inputId}
                            style={{ display: "none" }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) {
                                e.target.value = null;
                                return;
                              }
                              if (!isHeadOfficeDocumentSupportedFile(file)) {
                                Swal.fire(
                                  "지원하지 않는 파일 형식",
                                  "이미지, PDF, Excel(xls/xlsx) 파일만 첨부할 수 있습니다.",
                                  "warning"
                                );
                                e.target.value = null;
                                return;
                              }
                              handleCellChange(file);
                              e.target.value = null;
                            }}
                          />

                          {hasImage ? (
                            <div style={rowActionSx}>
                              {typeof value === "string" && (
                                <Tooltip title="다운로드">
                                  <IconButton
                                    size="small"
                                    sx={iconBtnSx}
                                    onClick={() => handleDownload(value)}
                                  >
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              <Tooltip title="미리보기">
                                <IconButton
                                  size="small"
                                  sx={{ ...iconBtnSx, color: isChanged ? "#d32f2f" : iconBtnSx.color }}
                                  onClick={() => handleViewImage(row.original, colKey)}
                                >
                                  <ImageSearchIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <label htmlFor={inputId} style={{ display: "inline-flex" }}>
                                <MDButton
                                  size="small"
                                  component="span"
                                  color="info"
                                  sx={actionBtnSx}
                                >
                                  변경
                                </MDButton>
                              </label>
                            </div>
                          ) : (
                            <div style={rowActionSx}>
                              <label htmlFor={inputId} style={{ display: "inline-flex" }}>
                                <MDButton
                                  size="small"
                                  component="span"
                                  color="info"
                                  sx={actionBtnSx}
                                >
                                  업로드
                                </MDButton>
                              </label>
                            </div>
                          )}
                        </td>
                      );
                    }

                    return (
                      <td
                        key={cell.id}
                        style={{
                          ...(isColumnWidthLocked
                            ? { width: cellWidth, minWidth: cellWidth, maxWidth: cellWidth }
                            : {}),
                          textAlign: CENTER_ALIGN_COLS.has(colKey)
                            ? "center"
                            : colKey === "salary"
                              ? "right"
                              : "left",
                        }}
                        contentEditable={
                          isEditable &&
                          !isSelect &&
                          !isDate &&
                          canEditMaskedSensitiveField
                        }
                        suppressContentEditableWarning
                        className={isEditable && isChanged ? "edited-cell" : ""}
                        onFocus={
                          isEditable &&
                          !isSelect &&
                          !isDate &&
                          canEditMaskedSensitiveField
                            ? (e) => {
                              lockColumnsForEditing();
                              keepEditableTailVisible(e.currentTarget);
                            }
                            : undefined
                        }
                        onInput={
                          isEditable &&
                          !isSelect &&
                          !isDate &&
                          canEditMaskedSensitiveField
                            ? (e) => keepEditableTailVisible(e.currentTarget)
                            : undefined
                        }
                        onBlur={
                          isEditable &&
                            !isSelect &&
                            !isDate &&
                            canEditMaskedSensitiveField
                            ? (e) => {
                              let newValue = e.target.innerText.trim();
                              if (isNumeric) newValue = parseNumber(newValue);
                              handleCellChange(newValue);
                              if (isNumeric) e.currentTarget.innerText = formatNumber(newValue);
                            }
                            : undefined
                        }
                      >
                        {isSelect ? (
                          colKey === "idx" ? (
                            <Autocomplete
                              size="small"
                              options={workSystemOptions}
                              value={(() => {
                                const v = String(currentValue ?? "");
                                return workSystemOptionMap.get(v) || null;
                              })()}
                              onChange={(_, opt) => handleCellChange(opt ? opt.value : "")}
                              getOptionLabel={(opt) => opt?.label ?? ""}
                              isOptionEqualToValue={(opt, val) => opt.value === val.value}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="standard"
                                  placeholder="검색"
                                  InputProps={{ ...params.InputProps, disableUnderline: true }}
                                  inputProps={{
                                    ...params.inputProps,
                                    style: {
                                      fontSize: "12px",
                                      padding: 0,
                                      color: isChanged ? "#d32f2f" : "inherit",
                                      fontWeight: isChanged ? 600 : 400,
                                    },
                                  }}
                                />
                              )}
                              sx={{
                                width: "100%",
                                "& .MuiInputBase-root": { minHeight: 24 },
                                "& .MuiAutocomplete-input": {
                                  fontSize: "12px",
                                  padding: "0px !important",
                                  color: isChanged ? "#d32f2f" : "inherit",
                                  fontWeight: isChanged ? 600 : 400,
                                },
                                "& .MuiSvgIcon-root": {
                                  fontSize: 18,
                                  color: isChanged ? "#d32f2f" : "inherit",
                                },
                              }}
                            />
                          ) : (
                            <select
                              value={currentValue ?? ""}
                              onChange={(e) => handleCellChange(e.target.value)}
                              className={isChanged ? "edited-cell" : ""}
                              style={{
                                width: "100%",
                                background: "transparent",
                                cursor: "pointer",
                                border: "none",
                              }}
                            >
                              {colKey === "cor_type" &&
                                COR_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              {colKey === "account_id" &&
                                accountSelectOptions.map((acc) => (
                                  <option key={acc.value} value={acc.value}>
                                    {acc.label}
                                  </option>
                                ))}
                              {colKey === "use_yn" &&
                                USE_YN_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              {colKey === "position_type" &&
                                POSITION_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              {colKey === "contract_type" &&
                                CONTRACT_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              {colKey === "start_time" && (
                                <>
                                  <option value="">없음</option>
                                  {START_TIMES.map((t) => (
                                    <option key={t} value={t}>
                                      {t}
                                    </option>
                                  ))}
                                </>
                              )}
                              {colKey === "end_time" && (
                                <>
                                  <option value="">없음</option>
                                  {END_TIMES.map((t) => (
                                    <option key={t} value={t}>
                                      {t}
                                    </option>
                                  ))}
                                </>
                              )}
                            </select>
                          )
                        ) : isDate ? (
                          <input
                            type="date"
                            value={formatDateForInput(currentValue)}
                            onChange={(e) => handleCellChange(e.target.value)}
                            className={isChanged ? "edited-cell" : ""}
                          />
                        ) : (
                          (isNumeric
                            ? formatNumber(currentValue)
                            : maskSensitiveFieldValue(
                              colKey,
                              currentValue,
                              maskingEnabled,
                              maskingRole
                            )) ?? ""
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </MDBox>
    );
  };

  if (loading) return <LoadingScreen />;

  return (
    <>
      <MDBox
        pt={1}
        pb={1}
        sx={{
          display: "flex",
          justifyContent: isTabletOrSmallDesktop ? "flex-start" : "flex-end",
          alignItems: "center",
          gap: isTabletOrSmallDesktop ? 1 : 2,
          flexWrap: isTabletOrSmallDesktop ? "wrap" : "nowrap",
          position: "sticky",
          zIndex: 10,
          top: 78,
          backgroundColor: "#ffffff",
        }}
      >
        <TextField
          select
          size="small"
          value={activeStatus}
          onChange={(e) => {
            setLoading(true);
            setActiveStatus(e.target.value);
          }}
          sx={{ minWidth: 150 }}
          SelectProps={{ native: true }}
        >
          <option value="D">진행중</option>
          <option value="Y">채용확정</option>
          <option value="N">채용취소</option>
        </TextField>

        <Autocomplete
          size="small"
          sx={{
            minWidth: 200,
            ...(isTabletOrSmallDesktop ? { flex: "1 1 220px" } : {}),
          }}
          options={accountOptionsIndexed}
          value={selectedAccountOption}
          onChange={(_, opt) => {
            // 입력 비움 시 거래처 선택 유지
            if (!opt) return;
            setLoading(true);
            setSelectedAccountId(opt.value);
          }}
          onInputChange={(_, newValue) => {
            accountInputRef.current = newValue;
          }}
          getOptionLabel={(opt) => opt?.label ?? ""}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          filterOptions={(options, state) => {
            const q = (state.inputValue ?? "").trim().toLowerCase();
            if (!q) return options;
            return options.filter((o) => o._labelLower.includes(q));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="거래처 검색"
              placeholder="거래처명을 입력"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  selectAccountByInput();
                }
              }}
              sx={{
                "& .MuiInputBase-root": { height: 35, fontSize: 12 },
                  "& .MuiInputLabel-root": { fontSize: 12 },
                "& input": { paddingLeft: "8px", paddingTop: 0, paddingBottom: 0, lineHeight: 1 },
              }}
            />
          )}
        />

        <MDButton variant="gradient" color="success" onClick={handleAddRow}>
          행추가
        </MDButton>

        <MDButton
          variant="gradient"
          color="dark"
          onClick={handleExcelDownload}
          disabled={excelDownloading}
        >
          {excelDownloading ? "다운로드 중..." : "엑셀 다운로드"}
        </MDButton>

        <MDButton
          variant="outlined"
          color={maskingEnabled ? "dark" : "secondary"}
          onClick={handleToggleMasking}
        >
          {maskingEnabled ? "* 해제" : "* 적용"}
        </MDButton>

        <MDButton variant="gradient" color="info" onClick={handleSave} disabled={saving || loading}>
          {saving ? "저장중..." : "저장"}
        </MDButton>
      </MDBox>

      <MDBox pt={1} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            {renderTable(table)}
          </Grid>
        </Grid>
      </MDBox>

      {/* 첨부파일 공통 미리보기 오버레이 */}
      <PreviewOverlay
        open={previewFiles.length > 0}
        files={previewFiles}
        currentIndex={previewIndex}
        onChangeIndex={setPreviewIndex}
        onClose={handleCloseViewer}
      />
    </>
  );
}

export default AccountMemberRecSheet;
